import { CodeHighlight } from '@mantine/code-highlight';
import { Card, Flex, Loader, SimpleGrid, Text, Title } from '@mantine/core';
import { ExternalLink } from '../../components/ExternalLink';
import { PageWrapper } from '../../components/PageWrapper';
import { useSdkCalculators } from '../../hooks/use-sdk-calculators';
import { useVersion } from '../../versions/url-version';
import { getExampleSnippet } from './snippets';

export function Package() {
  const version = useVersion();
  const { loading, error, calculators } = useSdkCalculators(version);

  return (
    <PageWrapper>
      <Title order={2}>Using the NPM package</Title>
      <p>
        The AIA Emissions Calculators are available as an NPM package. You can install the package using the following
        command:
      </p>
      <CodeHighlight language="bash" code="npm install @aginnovationaustralia/emissions-calculators" />
      <p>
        Once installed, you can use the package to perform emissions calculations using any of the supported
        calculators. Each calculator has a specific input schema that you need to provide to the calculator function.
      </p>

      <p>This version for the package supports the following calculators:</p>
      {loading ? (
        <Flex justify="center" align="center" w="100%" h="200px">
          <Loader size="lg" />
          <Text ml="md">Loading calculators...</Text>
        </Flex>
      ) : error ? (
        <Text c="red">{error}</Text>
      ) : (
        <SimpleGrid cols={2}>
          {(calculators ?? []).map((calculator) => (
            <Card withBorder bg="var(--color-bg-secondary)" key={calculator}>
              {calculator}
            </Card>
          ))}
        </SimpleGrid>
      )}

      <br />

      <Title order={3}>Example</Title>
      <p>
        Here's a simple example showing how to use the package to perform an emissions calculation with the Grains
        calculator. You can see the key steps of:
      </p>
      <ul>
        <li>Creating an input object with the data for the calculator</li>
        <li>Validating the input</li>
        <li>Performing the emissions calculation</li>
      </ul>
      <CodeHighlight
        withExpandButton
        defaultExpanded
        language="tsx"
        code={getExampleSnippet(version)}
      />
      <br />
      <Title order={4}>Node vs Browser execution</Title>
      <p>
        The package can be used in both Node.js and the browser. There are 2 matching sets of calculator functions
        exported that will work in their respective environments. The browser specific calculators are exported from the
        package using the{' '}
        <ExternalLink href="https://docs.npmjs.com/cli/v11/configuring-npm/package-json#browser">browser</ExternalLink>{' '}
        field in the package.json file, and should be picked up automatically by the bundler.
      </p>

      <p>
        In either context, every calculator function accepts an optional <code>options</code> parameter that controls
        some common behaviour of the calculator. The options are:
      </p>
      <ul>
        <li>
          <code>disableMetrics</code> - If you set this to <code>true</code>, the calculator will not collect metrics
          about the calculation execution. This is useful if you are using the calculator in a production environment
          and do not want to collect metrics.
        </li>
        <li>
          <code>organisation</code> - If you set this with a value, it will be included in the metrics that are
          reported.
        </li>
      </ul>

      <Title order={4}>Browser configuration</Title>
      <p>
        When running the calculators in the browser, the only configuration available is to pass the{' '}
        <code>options</code> parameter to the calculator function.
      </p>

      <Title order={4}>Node.js configuration</Title>
      <p>
        When running the calculators via Node.js, you can also control configuration via environment variables:
        <ul>
          <li>
            <code>DISABLE_CALCULATOR_METRICS</code> - If you set this to <code>true</code>, the calculator will not
            collect metrics about the calculation execution. This is useful if you are using the calculator in a
            production environment and do not want to collect metrics.
          </li>
          <li>
            <code>CALCULATOR_METRICS_ORGANISATION</code> - If you set this with a value, it will be included in the
            metrics that are reported.
          </li>
        </ul>
      </p>

      <Title order={3}>Data and metrics collection</Title>
      <p>
        The package includes functionality to collect anonymous metrics on how often features are being used. The data
        collected is not associated with specific users or organisations, and includes no personally identifying
        information like email addresses. It is only to understand the runtime adoption and usage of each calculator. If
        you don't want the library to report details of which calculators are being used, all reporting can be disabled
        (see above). The tool used to collect the usage metrics is{' '}
        <ExternalLink href="https://mixpanel.com/home/">MixPanel</ExternalLink>. The specific details that are collected
        is limited to:
        <ul>
          <li>the calculator being used</li>
          <li>the version of the calculator</li>
          <li>the NPM package version in use</li>
          <li>whether the calculator succeeded or failed</li>
          <li>if you define it, the organisation using the package</li>
          <li>
            the MixPanel client will include the IP address of the machine sending the metrics, which in turn is used
            for basic geolocation of the origin
          </li>
        </ul>
      </p>

      <Title order={3}>GitHub repository</Title>
      <p>
        The calculator implementation is fully open source and available as a{' '}
        <ExternalLink href="https://github.com/aginnovationaustralia/emissions-calculators">
          GitHub repository
        </ExternalLink>
        . This is the best place to get involved in the project, report issues, or contribute changes.
      </p>

      <Title order={3}>Installing multiple versions of the package</Title>
      <p>
        The package is published to NPM with a specific version number. If you need to use a different version of the
        package, you can install it using the following command:
      </p>
      <CodeHighlight language="bash" code="npm install @aginnovationaustralia/emissions-calculators@3.0.0" />
      <p>
        If you want to build an application that supports multiple versions of the package, you can use NPM alias syntax
        to install multiple versions of the package.
        <CodeHighlight
          withExpandButton
          defaultExpanded
          language="json"
          code={`
          "dependencies": {
    "calcV300": "npm:@aginnovationaustralia/emissions-calculators@^3.0.0",
    "calcV301": "npm:@aginnovationaustralia/emissions-calculators@^3.0.1"
  }
        `}
        />
        <CodeHighlight
          withExpandButton
          defaultExpanded
          language="tsx"
          code={`
          import {
  BeefInput as BeefInput300,
  BeefOutput as BeefOutput300,
  calculateBeef as calculateBeef300,
} from "calcV300";
import {
  BeefInput as BeefInput301,
  BeefOutput as BeefOutput301,
  calculateBeef as calculateBeef301,
} from "calcV301";
        `}
        />
      </p>
      <p>
        A complete example is available in the{' '}
        <ExternalLink href="https://github.com/aginnovationaustralia/emissions-calculators-examples/tree/main/examples/typescript-multiple-package-versions">
          emissions-calculators-examples
        </ExternalLink>{' '}
        GitHub repository.
      </p>
    </PageWrapper>
  );
}
